/**
 * Local-only daily streak counter.
 *
 * Per plan §Streak counter (v1):
 *   - Day boundary = user-local midnight (NOT UTC).
 *   - Resets on missed day.
 *   - No leaderboards, no social, no XP.
 *   - Multi-tab safe via storage event listener (matches JLPT App.tsx
 *     multi-tab pattern at App.tsx:storage event handler).
 *   - Private-mode disclaimer in footer ("Streaks reset when private
 *     browsing ends").
 *
 * Stored shape (key: `english-streak`):
 *   { count: number, lastActiveDay: string }
 *
 * `lastActiveDay` is YYYY-MM-DD in user-local timezone.
 */

import { signal, effect } from '@preact/signals';

const STORAGE_KEY = 'english-streak';

interface StreakState {
  count: number;
  lastActiveDay: string;
}

function todayKey(): string {
  const d = new Date();
  // Pad to YYYY-MM-DD
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function dayDiff(from: string, to: string): number {
  // Returns whole days from `from` to `to` (calendar-day-level).
  const fromDate = new Date(`${from}T00:00:00`);
  const toDate = new Date(`${to}T00:00:00`);
  return Math.round((toDate.getTime() - fromDate.getTime()) / (24 * 60 * 60 * 1000));
}

function readFromStorage(): StreakState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (typeof parsed.count === 'number' && typeof parsed.lastActiveDay === 'string') {
        return parsed;
      }
    }
  } catch {
    // Private mode or corrupt — fall through to default.
  }
  return { count: 0, lastActiveDay: '' };
}

function writeToStorage(state: StreakState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Private mode: best-effort, no recovery.
  }
}

// ============================================
// Public API
// ============================================

export const streakCount = signal(0);
export const streakLastActiveDay = signal<string>('');

/**
 * Initialize the streak signal from localStorage. Call once at app boot
 * before rendering the streak UI. Returns the hydrated state for callers
 * that want to chain it.
 */
export function initStreak(): StreakState {
  const state = readFromStorage();
  streakCount.value = state.count;
  streakLastActiveDay.value = state.lastActiveDay;

  // Multi-tab sync: re-hydrate on storage events from other tabs.
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', e => {
      if (e.key === STORAGE_KEY) {
        const fresh = readFromStorage();
        streakCount.value = fresh.count;
        streakLastActiveDay.value = fresh.lastActiveDay;
      }
    });
  }

  return state;
}

/**
 * Record activity for today. Increments the streak if this is a new
 * consecutive day; resets to 1 if the user missed a day; no-ops if the
 * user already studied today.
 *
 * Returns the post-update state.
 */
export function markActiveToday(): StreakState {
  const today = todayKey();
  const last = streakLastActiveDay.value;

  if (last === today) {
    // Already counted today.
    return { count: streakCount.value, lastActiveDay: last };
  }

  let newCount: number;
  if (last === '') {
    // First-ever activity.
    newCount = 1;
  } else {
    const diff = dayDiff(last, today);
    if (diff === 1) {
      // Consecutive day.
      newCount = streakCount.value + 1;
    } else if (diff <= 0) {
      // Clock skew or DST oddity — keep streak (don't penalize).
      newCount = streakCount.value;
    } else {
      // Missed at least one day.
      newCount = 1;
    }
  }

  streakCount.value = newCount;
  streakLastActiveDay.value = today;

  const state = { count: newCount, lastActiveDay: today };
  writeToStorage(state);
  return state;
}

/**
 * Reset the streak (for testing or "I want to start over" UX).
 */
export function resetStreak(): void {
  streakCount.value = 0;
  streakLastActiveDay.value = '';
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Private mode: no-op.
  }
}

// ============================================
// Auto-persist on count changes (defensive — markActiveToday already writes)
// ============================================

effect(() => {
  // Only persist if we have a meaningful state. Initial signal value of 0/'' is the default.
  if (streakLastActiveDay.value !== '') {
    writeToStorage({
      count: streakCount.value,
      lastActiveDay: streakLastActiveDay.value
    });
  }
});
